import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { Spinner } from '../../components/Spinner'
import { useMediaSrc } from '../../library/useMediaSrc'
import { formatDuration, formatUsd, type Playable } from '../data'
import { useFanslyDeps } from '../deps'

interface ThumbProps {
  /** What to show: the media itself, or its preview when locked. */
  media: Playable | null
  /** A price badge (dollars) for paid items. */
  price?: number
  /** Locked to the viewer (shows a lock over the preview). */
  locked?: boolean
  /** A count badge (a bundle's items). */
  count?: number
  /** Opens the item (viewer, selection, …). */
  onOpen?: () => void
  /** Selection state for pickers. */
  selected?: boolean
  label?: string
  className?: string
}

/**
 * One Fansly media tile: the poster loads when it scrolls into view (through
 * the CRM when Fansly locked the URL to the account's IP), with kind, price,
 * lock and count badges.
 */
export function FsThumb({ media, price = 0, locked = false, count, onOpen, selected, label, className = '' }: ThumbProps) {
  const { t, i18n } = useTranslation('fansly')
  const url = media?.poster || (media?.kind === 'image' ? media.src : '')
  const { ref, src, blocked } = useMediaSrc(url)
  const Tag = onOpen ? 'button' : 'div'
  return (
    <Tag
      ref={ref as React.RefObject<HTMLButtonElement & HTMLDivElement>}
      type={onOpen ? 'button' : undefined}
      className={`fs-thumb ${selected ? 'is-selected' : ''} ${locked ? 'is-locked' : ''} ${className}`}
      onClick={onOpen}
      aria-label={label ?? t(`media.kind.${media?.kind ?? 'image'}`)}
      aria-pressed={selected === undefined ? undefined : selected}
    >
      {src ? (
        <img className="fs-thumb-img" src={src} alt="" draggable={false} />
      ) : (
        <span className="fs-thumb-ph" aria-hidden="true">
          <FeatherIcon name={blocked ? 'image' : media?.kind === 'audio' ? 'music' : media?.kind === 'video' ? 'film' : 'image'} size={22} />
        </span>
      )}
      {media?.kind === 'video' && (
        <span className="fs-thumb-badge fs-thumb-kind">
          <FeatherIcon name="play" size={11} />
          {media.duration ? formatDuration(media.duration) : null}
        </span>
      )}
      {media?.kind === 'audio' && (
        <span className="fs-thumb-badge fs-thumb-kind">
          <FeatherIcon name="mic" size={11} />
          {media.duration ? formatDuration(media.duration) : null}
        </span>
      )}
      {count && count > 1 ? <span className="fs-thumb-badge fs-thumb-count">{count}</span> : null}
      {price > 0 && <span className="fs-thumb-badge fs-thumb-price">{formatUsd(price, i18n.language)}</span>}
      {locked && (
        <span className="fs-thumb-lock" aria-hidden="true">
          <FeatherIcon name="lock" size={18} />
        </span>
      )}
      {selected && (
        <span className="fs-thumb-check" aria-hidden="true">
          <FeatherIcon name="check" size={14} />
        </span>
      )}
    </Tag>
  )
}

/** A full-screen viewer for one item; video/audio stream through the CRM. Esc or the backdrop closes. */
export function FsViewer({ media, onClose, onPrev, onNext }: { media: Playable; onClose: () => void; onPrev?: () => void; onNext?: () => void }) {
  const { t } = useTranslation('fansly')
  const { streamMedia, loadMedia } = useFanslyDeps()
  const [src, setSrc] = useState('')
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') onPrev?.()
      else if (e.key === 'ArrowRight') onNext?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, onPrev, onNext])

  useEffect(() => {
    let live = true
    setSrc('')
    setFailed(false)
    const url = media.src || media.poster
    if (!url) {
      setFailed(true)
      return
    }
    const done = (s: string) => live && setSrc(s)
    const fail = () => live && setFailed(true)
    if (media.kind === 'image') {
      loadMedia(url)
        .then((s) => (s.kind === 'blocked' ? fail() : done(s.src)))
        .catch(fail)
    } else {
      streamMedia(url).then(done).catch(fail)
    }
    return () => {
      live = false
    }
  }, [media, loadMedia, streamMedia])

  return (
    <div className="fs-viewer" role="dialog" aria-modal="true" aria-label={t('media.viewer')} onClick={onClose}>
      <button type="button" className="fs-viewer-close" aria-label={t('close')} onClick={onClose}>
        <FeatherIcon name="x" size={22} />
      </button>
      {onPrev && (
        <button type="button" className="fs-viewer-nav fs-viewer-prev" aria-label={t('media.previous')} onClick={(e) => (e.stopPropagation(), onPrev())}>
          <FeatherIcon name="chevron-left" size={26} />
        </button>
      )}
      {onNext && (
        <button type="button" className="fs-viewer-nav fs-viewer-next" aria-label={t('media.next')} onClick={(e) => (e.stopPropagation(), onNext())}>
          <FeatherIcon name="chevron-right" size={26} />
        </button>
      )}
      <div className="fs-viewer-stage" onClick={(e) => e.stopPropagation()}>
        {failed ? (
          <div className="fs-viewer-msg">{t('media.unavailable')}</div>
        ) : !src ? (
          <Spinner size={32} label={t('loading')} />
        ) : media.kind === 'image' ? (
          <img className="fs-viewer-media" src={src} alt="" />
        ) : media.kind === 'video' ? (
          <video className="fs-viewer-media" src={src} controls autoPlay playsInline controlsList="nodownload noremoteplayback" disablePictureInPicture />
        ) : (
          <audio className="fs-viewer-audio" src={src} controls autoPlay controlsList="nodownload" />
        )}
      </div>
    </div>
  )
}
